<template>
  <div class="login-page">
    <div class="background f-c">
      <div class="top"></div>
      <div class="bottom"></div>
    </div>
    <div class="center f-c-c-c">
      <div class="center-card">
        <div class="f-r-c-c company-title">
          <svg-icon icon-class="rii" class="icon" />
          <span class="title">RII</span>
        </div>
        <div class="login-form f-c">
          <span v-if="otherError" class="error-msg">{{ otherErrorMsg }}</span>
          <div class="f-c" style="height: 56px;">
            <el-input
              v-model="email"
              type="email"
              clearable
              @input="emailInput"
              placeholder="Email"
            >
              <template slot="prepend">Email</template>
            </el-input>
            <span v-if="emailError" class="error-msg"> Email is required </span>
          </div>
          <div class="f-c" style="height: 56px;">
            <el-input
              v-model="password"
              placeholder="Password"
              show-password
              clearable
              @input="passwordInput"
            >
              <template slot="prepend">Password</template>
            </el-input>
            <span v-if="passwordError" class="error-msg">
              password is required
            </span>
          </div>
          <div class="f-c" style="height: 56px;">
            <el-input
              v-model="verifyCode"
              placeholder="Verify code"
              type="number"
              clearable
              @input="passwordInput"
            >
              <template slot="prepend">Passcode</template>
            </el-input>
            <span v-if="verifyCodeError" class="error-msg">
              Verify code is required
            </span>
          </div>
          <div class="f-c buttons-group">
            <el-button
              :disabled="timeCount > 0"
              type="primary"
              @click="sentVerifyCode"
            >
              <span v-if="timeCount === 0">Send Verify Code</span>
              <span v-else>Resend after {{ timeCount }} seconds</span>
            </el-button>
            <el-button type="primary" @click="doLogin" :loading="loading">
              Login
            </el-button>
          </div>
          <div class="f-r-hsb" style="gap: 10px;">
            <el-checkbox class="remember" v-model="rememberMe">
              Remember me
            </el-checkbox>
            <a href="/support" class="support">Forget password?</a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import SvgIcon from '@/components/common/SvgIcon.vue'
import { login, sendVerificationCode } from '@/api/user'
import { setRefreshToken, setToken } from '@/core/utils/token'
import {
  getIsRememberUsername,
  getRememberedUsername,
  removeRememberedUsername,
  setIsRememberUsername,
  setRememberedUsername
} from '@/core/models/user'
export default {
  name: 'LoginPage',
  components: { SvgIcon },
  data() {
    return {
      email: '',
      password: '',
      verifyCode: '',
      emailError: false,
      passwordError: false,
      verifyCodeError: false,
      otherError: false,
      rememberMe: false,
      timeCount: 0,
      timeCountInterval: null,
      otherErrorMsg: '',
      loading: false,
      loadingCode: false
    }
  },
  watch: {
    timeCount() {
      sessionStorage.setItem('codeSentCount', this.timeCount)
    },
    rememberMe() {
      setIsRememberUsername(this.rememberMe)
      if (!this.rememberMe) removeRememberedUsername()
    }
  },
  mounted() {
    let prev = parseInt(sessionStorage.getItem('codeSentCount'))
    if (isNaN(prev) || prev < 0) prev = 0
    this.timeCount = prev
    if (this.timeCount > 0) {
      this.startResendCount()
    }
    // check whether username is remembered
    this.rememberMe = getIsRememberUsername()
    if (this.rememberMe) {
      this.email = getRememberedUsername() ?? ''
    }
  },
  methods: {
    sentVerifyCode() {
      if (!this.email || this.email === '') {
        this.emailError = true
        return
      }
      this.loadingCode = true
      this.timeCount = 30
      sendVerificationCode(this.email)
        .then(res => {
          if (res) {
            this.startResendCount()
          }
        })
        .catch(() => {
          this.verifyCodeError = true
          this.clearTimedCount()
          this.timeCount = 0
        })
        .finally(() => {
          this.loadingCode = false
        })
    },
    doLogin() {
      if (!this.email || this.email === '') {
        this.emailError = true
        return
      }
      if (!this.password || this.password === '') {
        this.passwordError = true
        return
      }
      if (!this.verifyCode || this.verifyCode === '') {
        this.verifyCodeError = true
        return
      }
      // check user input
      this.loading = true
      login({
        username: this.email,
        password: this.password,
        code: this.verifyCode
      })
        .then(res => {
          setToken(res.token)
          setRefreshToken(res.refreshToken)
          this.otherError = false
          this.otherErrorMsg = ''
          this.$router.push({ path: '/' })
          this.loading = false
          sessionStorage.removeItem('codeSentCount')
          if (this.rememberMe) setRememberedUsername(this.email)
        })
        .catch(err => {
          this.otherError = true
          this.otherErrorMsg = err
          this.loading = false
        })
    },
    emailInput(val) {
      this.emailError = !val
    },
    passwordInput(val) {
      this.passwordError = !val
    },
    clearTimedCount() {
      if (this.timeCountInterval) clearInterval(this.timeCountInterval)
    },
    startResendCount() {
      this.clearTimedCount()
      this.timeCountInterval = setInterval(() => {
        this.timeCount -= 1
        if (this.timeCount === 0) clearInterval(this.timeCountInterval)
      }, 1000)
    }
  }
}
</script>
<style scoped lang="scss">
@import '@/assets/styles/common.scss';

.el-input {
  font-size: 16px !important;

  &::v-deep .el-input-group__prepend {
    color: $business-color;
    width: 90px;
    padding: 0 5px;
    text-align: center;
  }
}

.login-page {
  width: 100%;
  height: 100%;
  display: relative;

  .background {
    width: 100%;
    height: 100%;

    .top {
      background-color: $business-color;
      height: 50%;
    }

    .bottom {
      background-color: #85a8d4;
      height: 50%;
    }
  }

  .center {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;

    .center-card {
      .company-title {
        gap: 12px;
        margin-bottom: 30px;

        .title {
          user-select: none;
          text-align: center;
          color: white;
          font-size: 64px;
          font-weight: bolder;
          font-family: 'Times New Roman', Times, serif;
        }

        .icon {
          width: 56px;
          height: 56px;
          border-radius: 5px;
        }
      }

      .login-form {
        min-height: 250px !important;
        justify-content: center;
        padding: 40px;
        border-radius: 4px;
        background-color: white;
        gap: 5px;

        .el-input {
          width: 350px;
        }

        .support {
          font-size: 12px;
        }
      }
    }
  }
}

.buttons-group {
  margin-top: 20px;
  gap: 10px;

  .el-button {
    margin-left: 0;
    font-weight: bold;
  }
}

.remember ::v-deep .el-checkbox__label {
  font-size: 14px !important;
  color: #999 !important;
  margin-bottom: 10px;
}

.error-msg {
  font-size: 14px;
  color: red;
  word-break: break-all;
  max-width: 350px;
}
</style>
